import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Use ref to avoid stale closure issues with token access
  const tokenRef = useRef(localStorage.getItem("token"));

  const getAuthHeaders = useCallback(() => {
    const token = tokenRef.current || localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const initAuth = useCallback(async () => {
    const token = localStorage.getItem("token");
    tokenRef.current = token;
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (err) {
        localStorage.removeItem("token");
        tokenRef.current = null;
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    tokenRef.current = access_token;
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (name, email, password, phone) => {
    const response = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
      phone,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    tokenRef.current = access_token;
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    tokenRef.current = null;
    setUser(null);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    getAuthHeaders,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    isTrainer: user?.role === "trainer",
  }), [user, loading, login, register, logout, getAuthHeaders]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
